import React, { useState, useRef, useEffect } from "react";

import {
  Menu,
  X,
  CalendarDays,
  Users,
  DollarSign,
  TicketCheck,
  Settings,
  Building2,
  Globe,
  Image as ImageIcon,
  Camera,
  Sun,
  Moon,
} from "lucide-react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
// import { QrReader } from "react-qr-reader";
import { QrReader } from "@blackbox-vision/react-qr-reader";
import { db } from "../../firebase";
import { signOut } from "firebase/auth";
import firebase from "firebase/compat/app";
import {
  collection,
  doc,
  getDoc,
  updateDoc,
  query,
  getDocs,
  setDoc,
  orderBy,
  writeBatch,
  deleteDoc,
} from "firebase/firestore";
import { addDoc, serverTimestamp } from "firebase/firestore";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { auth } from "../../firebase";

const firebaseConfig = {
  apiKey: "AIzaSyBIu4pZw_GFUOZYknkd_jl1r3aSAV9n41g",
  authDomain: "connecticx-eca48.firebaseapp.com",
  projectId: "connecticx-eca48",
  storageBucket: "connecticx-eca48.appspot.com",
  messagingSenderId: "56674718618",
  appId: "1:56674718618:web:23565da9ecccacfb556023",
  measurementId: "G-LZQTWBPMSN",
};

firebase.initializeApp(firebaseConfig);

// Theme context
const ThemeContext = React.createContext();

const useTheme = () => {
  const context = React.useContext(ThemeContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};

// Dark mode toggle component
const DarkModeToggle = () => {
  const { darkMode, setDarkMode } = useTheme();

  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  return (
    <button
      onClick={toggleDarkMode}
      className="flex items-center gap-2 px-4 py-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
    >
      {darkMode ? (
        <Sun className="h-5 w-5 text-yellow-500" />
      ) : (
        <Moon className="h-5 w-5 text-gray-500" />
      )}
      <span className="text-sm font-medium">
        {darkMode ? "Light Mode" : "Dark Mode"}
      </span>
    </button>
  );
};

// Custom Card Components with theme support
const Card = ({ children, className = "", onClick }) => {
  const { darkMode } = useTheme();
  return (
    <div
      className={`${
        darkMode ? "bg-gray-800 text-white" : "bg-white"
      } rounded-lg shadow ${className}`}
      onClick={onClick}
    >
      {children}
    </div>
  );
};

const CardContent = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>{children}</div>
);

// QR Scanner Component
const QRScanner = () => {
  const [scannedCode, setScannedCode] = useState(null);
  const [attendeeName, setAttendeeName] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [scanStatus, setScanStatus] = useState(null); // 'success', 'expired', 'notfound', 'error'
  const [scanMessage, setScanMessage] = useState("");
  const [scanning, setScanning] = useState(true);

  const handleScan = async (result) => {
    if (!result || !scanning) return;

    setScanning(false); // Prevent multiple scans while processing

    try {
      const scannedData = JSON.parse(result.text);
      const { ticketId, attendeeName } = scannedData;
      setScannedCode(ticketId);
      setAttendeeName(attendeeName);

      // Check if the ticketId exists in Firestore
      const ticketRef = doc(db, "orders", ticketId);
      const ticketSnapshot = await getDoc(ticketRef);

      if (!ticketSnapshot.exists()) {
        setScanStatus("notfound");
        setScanMessage("Ticket not found in the database.");
        setTimeout(() => setScanning(true), 3000); // Allow scanning again after 3 seconds
        return;
      }

      const ticketData = ticketSnapshot.data();

      // Check if ticket has already been used
      if (ticketData.isExpired) {
        setScanStatus("expired");
        setScanMessage("This ticket has already been used and is expired.");
        setTimeout(() => setScanning(true), 3000); // Allow scanning again after 3 seconds
        return;
      }

      // Valid ticket found - show popup to confirm entry
      setScanStatus(null);
      setIsPopupOpen(true);
    } catch (error) {
      console.error("Error processing QR code:", error);
      setScanStatus("error");
      setScanMessage("Invalid QR code format.");
      setTimeout(() => setScanning(true), 3000); // Allow scanning again after 3 seconds
    }
  };

  const handlePopupClose = async (shouldAllow) => {
    try {
      if (shouldAllow) {
        // 1. Add to 'eventEntries' collection
        const eventEntryRef = doc(collection(db, "eventEntries"), scannedCode);
        await setDoc(eventEntryRef, {
          ticketId: scannedCode,
          attendeeName: attendeeName,
          timestamp: new Date(),
          allowed: true,
        });

        // 2. Update the ticket in 'orders' collection to mark it as expired
        const ticketRef = doc(db, "orders", scannedCode);
        await updateDoc(ticketRef, {
          isExpired: true,
          lastUsed: new Date(),
          lastEntryAllowed: true,
        });

        setScanStatus("success");
        setScanMessage("User has been authenticated to enter the event.");
      } else {
        // If entry is denied, we don't expire the QR code
        setScanStatus("denied");
        setScanMessage("Entry denied. Ticket remains valid for future use.");
      }
    } catch (error) {
      console.error("Error updating database:", error);
      setScanStatus("error");
      setScanMessage("Error updating ticket status. Please try again.");
    }

    setIsPopupOpen(false);

    // After processing, allow scanning again after a delay
    setTimeout(() => {
      setScanning(true);
      if (shouldAllow) {
        // Only reset the UI completely if the ticket was approved
        setTimeout(() => {
          setScannedCode(null);
          setAttendeeName(null);
          setScanStatus(null);
          setScanMessage("");
        }, 3000);
      }
    }, 1000);
  };

  const getStatusClass = () => {
    switch (scanStatus) {
      case "success":
        return "bg-green-100 text-green-800";
      case "expired":
        return "bg-red-100 text-red-800";
      case "notfound":
        return "bg-yellow-100 text-yellow-800";
      case "denied":
        return "bg-orange-100 text-orange-800";
      case "error":
        return "bg-red-100 text-red-800";
      default:
        return "bg-purple-100 text-purple-800";
    }
  };

  return (
    <div className="p-4">
      <h2 className="text-2xl mb-6">Ticket Scanner</h2>
      <div className="max-w-md mx-auto">
        <QrReader
          onResult={handleScan}
          onError={(error) => console.error(error)}
          constraints={{ facingMode: "environment" }}
          className="w-full"
        />

        {scannedCode && (
          <div className={`mt-4 p-4 rounded ${getStatusClass()}`}>
            <p className="font-semibold">Ticket ID: {scannedCode}</p>
            {attendeeName && <p>Attendee: {attendeeName}</p>}
            {scanStatus && <p className="mt-2 font-bold">{scanMessage}</p>}
          </div>
        )}

        <div className="mt-4 text-center text-sm text-gray-500">
          {scanning ? "Ready to scan..." : "Processing scan..."}
        </div>
      </div>

      {isPopupOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white p-6 rounded shadow-lg max-w-md w-full">
            <h3 className="text-xl font-bold mb-4">Allow entry?</h3>
            <p className="mb-2">Ticket found for:</p>
            <p className="font-bold text-lg mb-4">{attendeeName}</p>
            <p className="text-sm text-gray-600 mb-4">
              Note: Allowing entry will expire this ticket and prevent reuse.
              Denying entry will keep the ticket valid.
            </p>
            <div className="flex justify-end mt-4">
              <button
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded mr-2"
                onClick={() => handlePopupClose(false)}
              >
                Deny
              </button>
              <button
                className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
                onClick={() => handlePopupClose(true)}
              >
                Allow
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Image Upload Component
const ImageUpload = ({ onImageUpload }) => {
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    e.preventDefault();
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    if (file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = (e) => {
        onImageUpload(e.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload an image file");
    }
  };

  return (
    <div
      className={`border-2 border-dashed rounded-lg p-6 ${
        dragActive ? "border-purple-500 bg-purple-50" : "border-gray-300"
      }`}
      onDragEnter={handleDrag}
      onDragLeave={handleDrag}
      onDragOver={handleDrag}
      onDrop={handleDrop}
      onClick={() => inputRef.current.click()}
    >
      <input
        ref={inputRef}
        type="file"
        className="hidden"
        accept="image/*"
        onChange={handleChange}
      />
      <div className="flex items-center justify-center flex-col">
        <ImageIcon className="h-12 w-12 text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">
          {dragActive ? "Drop the image here" : "Drop files here to upload"}
        </p>
        <p className="text-xs text-gray-400">Image Size 1170x570</p>
      </div>
    </div>
  );
};

// Main Layout Component
const OrganizerDashboard = () => {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [darkMode, setDarkMode] = useState(false);
  const [history, setHistory] = useState(["dashboard"]);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  const [isAuthorized, setIsAuthorized] = useState(true);

  useEffect(() => {
    const auth = getAuth();
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {

      setIsAuthorized(true);
    });

    return () => unsubscribe();
  }, []);

  if (!isAuthorized) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-100 dark:bg-gray-900">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white">
            Access Denied
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            You do not have permission to view this page.
          </p>
        </div>
      </div>
    );
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      // Optionally, you can redirect the user or update app state
      // For example, you might want to use React Router or a state management solution
    } catch (error) {
      console.error("Logout error:", error);
      alert("Failed to log out. Please try again.");
    }
  };

  const handleTabChange = (tabId) => {
    setHistory([...history, tabId]);
    setActiveTab(tabId);
    setIsSidebarOpen(false);
  };

  const handleBack = () => {
    if (history.length > 1) {
      const newHistory = history.slice(0, -1);
      setHistory(newHistory);
      setActiveTab(newHistory[newHistory.length - 1]);
    }
  };

  const renderContent = () => {
    switch (activeTab) {
      case "event-management":
        return <EventManagement onBack={handleBack} />;
      case "event-bookings":
        return <EventBookings />;
      case "transactions":
        return <Transactions />;
      case "support-tickets":
        return <SupportTickets />;
      case "scanner":
        return <QRScanner />;
      default:
        return <DashboardContent onTabChange={handleTabChange} />;
    }
  };

  return (
    <ThemeContext.Provider value={{ darkMode, setDarkMode }}>
      <div
        className={`flex h-screen ${
          darkMode ? "bg-gray-900 text-white" : "bg-white text-gray-800"
        }`}
      >
        {/* Sidebar */}

        <div
          className={`z-10 fixed inset-0 bg-gray-800 text-white p-4 transition-transform duration-300 ${
            isSidebarOpen ? "translate-x-0" : "-translate-x-full"
          } md:relative md:translate-x-0 md:w-64 md:shadow-md`}
        >
          <nav className="mt-16 md:mt-6">
            <div className="flex flex-col items-center mb-6 md:hidden">
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-full hover:bg-gray-700 mb-4"
              >
                {isSidebarOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
              <DarkModeToggle />
            </div>
            <div className="hidden md:flex justify-between items-center mb-6">
              <DarkModeToggle />
              <button
                onClick={() => setIsSidebarOpen(!isSidebarOpen)}
                className="p-2 rounded-full hover:bg-gray-700 md:hidden"
              >
                {isSidebarOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </button>
            </div>
            <ul className="space-y-2">
              {[
                {
                  id: "dashboard",
                  label: "Dashboard",
                  icon: <Settings className="h-4 w-4" />,
                },
                {
                  id: "event-management",
                  label: "Event Management",
                  icon: <CalendarDays className="h-4 w-4" />,
                },
                {
                  id: "event-bookings",
                  label: "Event Bookings",
                  icon: <TicketCheck className="h-4 w-4" />,
                },
                {
                  id: "transactions",
                  label: "Transactions",
                  icon: <DollarSign className="h-4 w-4" />,
                },
                {
                  id: "support-tickets",
                  label: "Support Tickets",
                  icon: <Users className="h-4 w-4" />,
                },
                {
                  id: "scanner",
                  label: "Ticket Scanner",
                  icon: <Camera className="h-4 w-4" />,
                },
                {
                  id: "logout",
                  label: "Log Out",
                  icon: (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                      <polyline points="16 17 21 12 16 7" />
                      <line x1="21" x2="9" y1="12" y2="12" />
                    </svg>
                  ),
                },
              ].map((item) => (
                <li
                  key={item.id}
                  onClick={() => {
                    if (item.id === "logout") {
                      handleLogout();
                    } else {
                      handleTabChange(item.id);
                    }
                  }}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer ${
                    activeTab === item.id
                      ? "bg-purple-100 text-purple-600"
                      : "hover:bg-gray-700"
                  }`}
                >
                  {item.icon}
                  {item.label}
                </li>
              ))}
            </ul>
          </nav>
        </div>

        {/* Main content */}
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="flex justify-between items-center mb-4 md:hidden">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="p-2 rounded-full hover:bg-gray-200 dark:hover:bg-gray-700"
            >
              {isSidebarOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
          </div>
          {history.length > 1 && (
            <button
              onClick={handleBack}
              className="mb-4 text-purple-600 hover:text-purple-700 flex items-center gap-2 dark:text-purple-400 dark:hover:text-purple-300"
            >
              ← Back
            </button>
          )}
          {renderContent()}
        </div>
      </div>
    </ThemeContext.Provider>
  );
};

// Modified Event Management Component with image upload
const EventManagement = ({ onBack }) => {
  const [eventType, setEventType] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);
  const { darkMode } = useTheme();

  const handleCardClick = (type) => {
    setEventType(type);
    setShowForm(true);
  };

  const handleImageUpload = (imageData) => {
    setUploadedImage(imageData);
  };

  return (
    <div>
      <h2 className="text-2xl mb-6 flex items-center gap-2">
        <CalendarDays className="h-6 w-6" />
        Event Management
      </h2>

      {!showForm && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleCardClick("online")}
          >
            <CardContent className="flex items-center gap-4 p-6">
              <Globe className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="text-lg font-medium">Add Online Event</h3>
                <p
                  className={`${darkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Create a virtual event with online access
                </p>
              </div>
            </CardContent>
          </Card>

          <Card
            className="cursor-pointer hover:shadow-lg transition-shadow"
            onClick={() => handleCardClick("venue")}
          >
            <CardContent className="flex items-center gap-4 p-6">
              <Building2 className="h-8 w-8 text-purple-600" />
              <div>
                <h3 className="text-lg font-medium">Add Venue Event</h3>
                <p
                  className={`${darkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Create an in-person event at a physical location
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {showForm && (
        <div>
          {eventType === "online" ? (
            <OnlineEventForm
              uploadedImage={uploadedImage}
              onImageUpload={handleImageUpload}
            />
          ) : (
            <VenueEventForm
              uploadedImage={uploadedImage}
              onImageUpload={handleImageUpload}
            />
          )}
        </div>
      )}
    </div>
  );
};

const OnlineEventForm = ({ uploadedImage, onImageUpload }) => {
  const [formData, setFormData] = useState({
    dateType: "",
    startDate: "",
    startTime: "",
    endDate: "",
    eventTitle: "",
    category: "",
    description: "",
    totalTickets: "",
    price: "",
    isFree: false,
    imageUrl: "",
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || { uid: 'mock-user', email: 'mock@example.com' });
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert("Please log in to create an event");
      return;
    }

    try {
      const eventData = {
        dateType: formData.dateType,
        startDate: formData.startDate,
        startTime: formData.startTime,
        endDate: formData.endDate,
        eventTitle: formData.eventTitle,
        category: formData.category,
        description: formData.description,
        totalTickets: Number(formData.totalTickets),
        price: formData.isFree ? 0 : Number(formData.price),
        isFree: formData.isFree,
        imageUrl: uploadedImage || "",
        eventType: "online",
        createdAt: serverTimestamp(),
        status: "active",
        // Add user information
        organizerId: user.uid,
        organizerEmail: user.email,
        lastUpdatedAt: serverTimestamp(),
      };

      // Validation
      const requiredFields = [
        "dateType",
        "startDate",
        "startTime",
        "endDate",
        "eventTitle",
        "category",
        "description",
        "totalTickets",
      ];

      const missingFields = requiredFields.filter((field) => !eventData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      const eventsRef = collection(db, "events");
      const docRef = await addDoc(eventsRef, eventData);

      console.log("Event created with ID: ", docRef.id);

      setFormData({
        dateType: "",
        startDate: "",
        startTime: "",
        endDate: "",
        eventTitle: "",
        category: "",
        description: "",
        totalTickets: "",
        price: "",
        isFree: false,
        imageUrl: "",
      });

      if (onImageUpload) onImageUpload(null);

      alert("Event created successfully!");
    } catch (error) {
      console.error("Error creating event: ", error);
      alert(`Error creating event: ${error.message}`);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${
        DarkModeToggle ? "bg-gray-800" : "bg-white"
      } p-6 rounded-lg shadow`}
    >
      <h3 className="text-xl font-medium mb-6">Add Online Event</h3>

      <div className="space-y-4">
        {/* Image Upload Section */}
        {uploadedImage ? (
          <div className="relative">
            <img
              src={uploadedImage}
              alt="Uploaded event"
              className="w-full h-64 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={() => onImageUpload(null)}
              className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full"
            >
              ×
            </button>
          </div>
        ) : (
          <ImageUpload onImageUpload={onImageUpload} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">Date Type*</label>
            <select
              name="dateType"
              value={formData.dateType}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select Date Type</option>
              <option value="single">Single Day</option>
              <option value="multiple">Multiple Days</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Start Date*
            </label>
            <input
              type="date"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Start Time*
            </label>
            <input
              type="time"
              name="startTime"
              value={formData.startTime}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">End Date*</label>
            <input
              type="date"
              name="endDate"
              value={formData.endDate}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Event Title*
            </label>
            <input
              type="text"
              name="eventTitle"
              value={formData.eventTitle}
              onChange={handleInputChange}
              placeholder="Enter Event Name"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Category*</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select Category</option>
              <option value="conference">Conference</option>
              <option value="workshop">Workshop</option>
              <option value="webinar">Webinar</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description*</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            className="w-full p-2 border rounded"
            required
          ></textarea>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Total Available Tickets*
            </label>
            <input
              type="number"
              name="totalTickets"
              value={formData.totalTickets}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">
              Price (USD)*
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                name="price"
                value={formData.price}
                onChange={handleInputChange}
                placeholder="Enter Ticket Price"
                className="w-full p-2 border rounded"
                disabled={formData.isFree}
                required={!formData.isFree}
              />
              <label className="flex items-center gap-1">
                <input
                  type="checkbox"
                  name="isFree"
                  checked={formData.isFree}
                  onChange={handleInputChange}
                />
                Free
              </label>
            </div>
          </div>
        </div>
      </div>

      <button
        type="submit"
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        Save Event
      </button>
    </form>
  );
};

const VenueEventForm = ({ uploadedImage, onImageUpload }) => {
  const [formData, setFormData] = useState({
    eventTitle: "",
    category: "",
    address: "",
    country: "",
    state: "",
    city: "",
    latitude: "",
    longitude: "",
    description: "",
    refundPolicy: "",
    imageUrl: "",
  });
  const [user, setUser] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser || { uid: 'mock-user', email: 'mock@example.com' });
    });

    return () => unsubscribe();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!user) {
      alert("Please log in to create an event");
      return;
    }

    try {
      const eventData = {
        eventTitle: formData.eventTitle,
        category: formData.category,
        address: formData.address,
        country: formData.country,
        state: formData.state,
        city: formData.city,
        latitude: formData.latitude ? Number(formData.latitude) : null,
        longitude: formData.longitude ? Number(formData.longitude) : null,
        description: formData.description,
        refundPolicy: formData.refundPolicy,
        imageUrl: uploadedImage || "",
        eventType: "venue",
        createdAt: serverTimestamp(),
        status: "active",
        // Add user information
        organizerId: user.uid,
        organizerEmail: user.email,
        lastUpdatedAt: serverTimestamp(),
      };

      const requiredFields = [
        "eventTitle",
        "category",
        "address",
        "country",
        "city",
        "description",
      ];

      const missingFields = requiredFields.filter((field) => !eventData[field]);
      if (missingFields.length > 0) {
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      const eventsRef = collection(db, "events");
      const docRef = await addDoc(eventsRef, eventData);

      console.log("Venue event created with ID: ", docRef.id);

      setFormData({
        eventTitle: "",
        category: "",
        address: "",
        country: "",
        state: "",
        city: "",
        latitude: "",
        longitude: "",
        description: "",
        refundPolicy: "",
        imageUrl: "",
      });

      if (onImageUpload) onImageUpload(null);

      alert("Venue event created successfully!");
    } catch (error) {
      console.error("Error creating venue event: ", error);
      alert(`Error creating venue event: ${error.message}`);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${
        DarkModeToggle ? "bg-gray-800" : "bg-white"
      } p-6 rounded-lg shadow`}
    >
      <h3 className="text-xl font-medium mb-6">Add Venue Event</h3>

      <div className="space-y-4">
        {/* Image Upload Section */}
        {uploadedImage ? (
          <div className="relative">
            <img
              src={uploadedImage}
              alt="Uploaded event"
              className="w-full h-64 object-cover rounded-lg"
            />
            <button
              type="button"
              onClick={() => onImageUpload(null)}
              className="absolute top-2 right-2 bg-red-500 text-white p-2 rounded-full"
            >
              ×
            </button>
          </div>
        ) : (
          <ImageUpload onImageUpload={onImageUpload} />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-1">
              Event Title*
            </label>
            <input
              type="text"
              name="eventTitle"
              value={formData.eventTitle}
              onChange={handleInputChange}
              placeholder="Enter Event Name"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Category*</label>
            <select
              name="category"
              value={formData.category}
              onChange={handleInputChange}
              className="w-full p-2 border rounded"
              required
            >
              <option value="">Select Category</option>
              <option value="conference">Conference</option>
              <option value="workshop">Workshop</option>
              <option value="concert">Concert</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Address*</label>
            <input
              type="text"
              name="address"
              value={formData.address}
              onChange={handleInputChange}
              placeholder="Enter Address"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Country*</label>
            <input
              type="text"
              name="country"
              value={formData.country}
              onChange={handleInputChange}
              placeholder="Enter Country"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">State</label>
            <input
              type="text"
              name="state"
              value={formData.state}
              onChange={handleInputChange}
              placeholder="Enter State"
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">City*</label>
            <input
              type="text"
              name="city"
              value={formData.city}
              onChange={handleInputChange}
              placeholder="Enter City"
              className="w-full p-2 border rounded"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Latitude</label>
            <input
              type="text"
              name="latitude"
              value={formData.latitude}
              onChange={handleInputChange}
              placeholder="Enter Latitude"
              className="w-full p-2 border rounded"
            />
          </div>

          <div>
            <label className="block text-sm font-medium mb-1">Longitude</label>
            <input
              type="text"
              name="longitude"
              value={formData.longitude}
              onChange={handleInputChange}
              placeholder="Enter Longitude"
              className="w-full p-2 border rounded"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">Description*</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows="4"
            className="w-full p-2 border rounded"
            required
          ></textarea>
        </div>

        <div>
          <label className="block text-sm font-medium mb-1">
            Refund Policy
          </label>
          <textarea
            name="refundPolicy"
            value={formData.refundPolicy}
            onChange={handleInputChange}
            rows="4"
            className="w-full p-2 border rounded"
          ></textarea>
        </div>
      </div>

      <button
        type="submit"
        className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
      >
        Save Event
      </button>
    </form>
  );
};

// Event Bookings Component
const EventBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchBookings = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;

        if (!user || user.email !== "mohammadnawaids.cse21@sbjit.edu.in") {
          setError("You do not have permission to view bookings.");
          setLoading(false);
          return;
        }

        const verifiedPaymentsQuery = query(collection(db, "verifiedPayments"));
        const querySnapshot = await getDocs(verifiedPaymentsQuery);

        const fetchedBookings = querySnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            amount: `₹${data.ticketPrice || 0}`,
            event: data.eventTitle,
            customer: `${data.customerName}`,
            paid: `₹${data.paymentDetails?.amount / 100 || 1}`,
            received: `₹${data.paymentDetails?.amount / 100 || 1}`,
            connectixReceived: `₹${data.paymentDetails?.amount / 100 || 1}`,
            via: "Razorpay",
            status: data.paymentStatus === "success" ? "Completed" : "Pending",
            orderId: data.orderId,
            email: data.customerEmail,
          };
        });

        setBookings(fetchedBookings);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching bookings:", err);
        setError("Failed to fetch bookings");
        setLoading(false);
      }
    };

    fetchBookings();
  }, [auth]);

  const handleViewTicket = async (booking) => {
    try {
      // Fetch the order details to get event information
      // const orderDoc = await getDoc(doc(db, "orders", booking.orderId));
      // const orderData = orderDoc.data();

      // Open a new window to display or download the ticket
      // You'll need to implement the ticket generation logic similar to your confirmation page
      window.open(`/confirmation/${booking.orderId}`, "_blank");
    } catch (err) {
      console.error("Error viewing ticket:", err);
      alert("Failed to retrieve ticket");
    }
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <p>Loading bookings...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl mb-6 flex items-center gap-2">Event Bookings</h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Booking ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Event
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Customer
              </th>
              {/* <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Cust. Paid
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Org. Received
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Connectix Received
              </th> */}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Paid Via
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {bookings.map((booking) => (
              <tr key={booking.id}>
                <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-gray-200">
                  {booking.id}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.event}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.customer}
                </td>
                {/* <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.paid}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.received}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.connectixReceived}
                </td> */}
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {booking.via}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      booking.status === "Completed"
                        ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100"
                    }`}
                  >
                    {booking.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm">
                  <button
                    onClick={() => handleViewTicket(booking)}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-500"
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            No bookings found
          </div>
        )}
      </div>
    </div>
  );
};

// Transactions Component
const Transactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const auth = getAuth();

  useEffect(() => {
    const fetchTransactions = async () => {
      try {
        if (!auth.currentUser) {
          setError("Please log in to view transactions");
          setLoading(false);
          return;
        }

        // First, fetch from orders collection
        const ordersQuery = query(collection(db, "orders"));
        const ordersSnapshot = await getDocs(ordersQuery);

        const fetchedTransactions = ordersSnapshot.docs.map((doc) => {
          const data = doc.data();
          return {
            id: doc.id,
            eventTitle: data.eventTitle,
            customerName: `${data.firstName} ${data.lastName}`,
            email: data.email,
            amount: `₹${data.ticketPrice || 0}`,
            method: "Razorpay",
            status: data.paymentStatus || "pending",
            phone: data.phone,
          };
        });

        setTransactions(fetchedTransactions);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to fetch transactions");
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [auth]);

  if (loading) {
    return (
      <div className="text-center py-6">
        <p>Loading transactions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-2xl mb-6 flex items-center gap-2">
        <DollarSign className="h-6 w-6" />
        Transactions
      </h2>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table className="min-w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Order ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Event
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Customer Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Phone
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Amount
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Payment Method
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Status
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {transactions.map((transaction) => (
              <tr key={transaction.id}>
                <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-900 dark:text-gray-200">
                  {transaction.id}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {transaction.eventTitle}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {transaction.customerName}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {transaction.email}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {transaction.phone}
                </td>
                <td className="px-4 py-3 text-sm text-green-600 dark:text-green-400">
                  {transaction.amount}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900 dark:text-gray-200">
                  {transaction.method}
                </td>
                <td className="px-4 py-3 text-sm">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      transaction.status === "success"
                        ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                        : "bg-yellow-100 text-yellow-800 dark:bg-yellow-800 dark:text-yellow-100"
                    }`}
                  >
                    {transaction.status === "success" ? "Completed" : "Pending"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {transactions.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            No transactions found
          </div>
        )}
      </div>
    </div>
  );
};

// Support Tickets Component
const SupportTickets = () => {
  const { darkMode } = useTheme();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    const fetchTickets = async () => {
      try {
        const ticketsQuery = query(
          collection(db, "supportTickets"),
          orderBy("createdAt", "desc")
        );

        const querySnapshot = await getDocs(ticketsQuery);

        const fetchedTickets = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
          createdAt: doc.data().createdAt?.toDate().toLocaleString() || "N/A",
        }));

        setTickets(fetchedTickets);
        setLoading(false);
      } catch (err) {
        console.error("Error fetching tickets:", err);
        setError("Failed to fetch support tickets");
        setLoading(false);
      }
    };

    fetchTickets();
  }, []);

  useEffect(() => {
    // Update selectAll state when all tickets are selected
    if (tickets.length > 0 && selectedTickets.length === tickets.length) {
      setSelectAll(true);
    } else {
      setSelectAll(false);
    }
  }, [selectedTickets, tickets]);

  const handleStatusUpdate = async (ticketId, newStatus) => {
    try {
      const ticketRef = doc(db, "supportTickets", ticketId);
      await updateDoc(ticketRef, {
        status: newStatus,
        lastUpdated: serverTimestamp(),
      });

      // Update local state
      setTickets(
        tickets.map((ticket) =>
          ticket.id === ticketId ? { ...ticket, status: newStatus } : ticket
        )
      );
    } catch (err) {
      console.error("Error updating ticket status:", err);
      alert("Failed to update ticket status");
    }
  };

  const handleTicketDelete = async (ticketId) => {
    const confirmed = window.confirm(
      "Are you sure you want to delete this ticket? This action cannot be undone."
    );

    if (!confirmed) return;

    try {
      // Delete from Firestore
      const ticketRef = doc(db, "supportTickets", ticketId);
      await deleteDoc(ticketRef);

      // Update local state
      setTickets(tickets.filter((ticket) => ticket.id !== ticketId));

      // Remove from selected tickets if it was selected
      if (selectedTickets.includes(ticketId)) {
        setSelectedTickets(selectedTickets.filter((id) => id !== ticketId));
      }

      alert("Ticket deleted successfully");
    } catch (err) {
      console.error("Error deleting ticket:", err);
      alert("Failed to delete ticket");
    }
  };

  const handleCheckboxChange = (ticketId) => {
    setSelectedTickets((prev) =>
      prev.includes(ticketId)
        ? prev.filter((id) => id !== ticketId)
        : [...prev, ticketId]
    );
  };

  const handleSelectAllChange = () => {
    if (selectAll) {
      setSelectedTickets([]);
    } else {
      setSelectedTickets(tickets.map((ticket) => ticket.id));
    }
    setSelectAll(!selectAll);
  };

  // Get IDs of tickets that match a specific status
  const getTicketIdsByStatus = (status) => {
    return tickets
      .filter((ticket) => ticket.status === status)
      .map((ticket) => ticket.id);
  };

  // Get currently selected ticket objects
  // const getSelectedTicketObjects = () => {
  //   return tickets.filter((ticket) => selectedTickets.includes(ticket.id));
  // };

  // Check if all selected tickets have the same status
  // const allSelectedTicketsHaveStatus = (status) => {
  //   const selectedTicketObjects = getSelectedTicketObjects();
  //   return (
  //     selectedTicketObjects.length > 0 &&
  //     selectedTicketObjects.every((ticket) => ticket.status === status)
  //   );
  // };

  // Filter selected tickets by status
  const filterSelectedTicketsByStatus = (status) => {
    return selectedTickets.filter(
      (ticketId) =>
        tickets.find((ticket) => ticket.id === ticketId)?.status === status
    );
  };

  const handleBulkStatusUpdate = async (newStatus) => {
    // Filter only tickets that need status change (avoid unnecessary updates)
    const ticketsToUpdate = filterSelectedTicketsByStatus(
      newStatus === "Open" ? "Closed" : "Open"
    );

    if (ticketsToUpdate.length === 0) {
      alert(`No tickets selected that can be marked as ${newStatus}`);
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to mark ${ticketsToUpdate.length} ticket(s) as ${newStatus}?`
    );

    if (!confirmed) return;

    try {
      // Create a batch for bulk operations
      const batch = writeBatch(db);

      // Add each ticket update to the batch
      ticketsToUpdate.forEach((ticketId) => {
        const ticketRef = doc(db, "supportTickets", ticketId);
        batch.update(ticketRef, {
          status: newStatus,
          lastUpdated: serverTimestamp(),
        });
      });

      // Commit the batch
      await batch.commit();

      // Update local state
      setTickets(
        tickets.map((ticket) =>
          ticketsToUpdate.includes(ticket.id)
            ? { ...ticket, status: newStatus }
            : ticket
        )
      );

      alert(`Successfully updated ${ticketsToUpdate.length} ticket(s)`);
    } catch (err) {
      console.error("Error performing bulk update:", err);
      alert("Failed to perform bulk update");
    }
  };

  const handleBulkDelete = async () => {
    if (selectedTickets.length === 0) {
      alert("Please select at least one ticket");
      return;
    }

    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedTickets.length} ticket(s)? This action cannot be undone.`
    );

    if (!confirmed) return;

    try {
      // Create a batch for bulk operations
      const batch = writeBatch(db);

      // Add each ticket deletion to the batch
      selectedTickets.forEach((ticketId) => {
        const ticketRef = doc(db, "supportTickets", ticketId);
        batch.delete(ticketRef);
      });

      // Commit the batch
      await batch.commit();

      // Update local state by removing deleted tickets
      setTickets(
        tickets.filter((ticket) => !selectedTickets.includes(ticket.id))
      );

      // Clear selections after bulk operation
      setSelectedTickets([]);

      alert(`Successfully deleted ${selectedTickets.length} ticket(s)`);
    } catch (err) {
      console.error("Error performing bulk delete:", err);
      alert("Failed to perform bulk delete");
    }
  };

  // Quick select functions
  const selectAllOpenTickets = () => {
    setSelectedTickets(getTicketIdsByStatus("Open"));
  };

  const selectAllClosedTickets = () => {
    setSelectedTickets(getTicketIdsByStatus("Closed"));
  };

  if (loading) {
    return (
      <div className="text-center py-6">
        <p>Loading tickets...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  // Count of open and closed tickets among selected
  const selectedOpenCount = filterSelectedTicketsByStatus("Open").length;
  const selectedClosedCount = filterSelectedTicketsByStatus("Closed").length;

  return (
    <div>
      <h2 className="text-2xl mb-6 flex items-center gap-2">
        <Users className="h-6 w-6" />
        Support Tickets
      </h2>

      {/* Bulk Action Controls */}
      {tickets.length > 0 && (
        <div className="mb-4">
          <div className="flex flex-wrap gap-2 mb-2">
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedOpenCount === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-blue-600 text-white hover:bg-blue-700"
              }`}
              onClick={() => handleBulkStatusUpdate("Closed")}
              disabled={selectedOpenCount === 0}
              title={
                selectedOpenCount === 0
                  ? "No open tickets selected"
                  : `Close ${selectedOpenCount} open tickets`
              }
            >
              Close Selected ({selectedOpenCount})
            </button>
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedClosedCount === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-green-600 text-white hover:bg-green-700"
              }`}
              onClick={() => handleBulkStatusUpdate("Open")}
              disabled={selectedClosedCount === 0}
              title={
                selectedClosedCount === 0
                  ? "No closed tickets selected"
                  : `Reopen ${selectedClosedCount} closed tickets`
              }
            >
              Reopen Selected ({selectedClosedCount})
            </button>
            <button
              className={`px-3 py-2 rounded text-sm ${
                selectedTickets.length === 0
                  ? "bg-gray-200 text-gray-500 cursor-not-allowed"
                  : "bg-red-600 text-white hover:bg-red-700"
              }`}
              onClick={handleBulkDelete}
              disabled={selectedTickets.length === 0}
            >
              Delete Selected ({selectedTickets.length})
            </button>
          </div>

          <div className="flex flex-wrap gap-2 text-sm">
            <button
              onClick={handleSelectAllChange}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              {selectAll ? "Deselect All" : "Select All"}
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={selectAllOpenTickets}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Select All Open
            </button>
            <span className="text-gray-400">|</span>
            <button
              onClick={selectAllClosedTickets}
              className="text-blue-600 hover:underline dark:text-blue-400"
            >
              Select All Closed
            </button>
            <span className="ml-auto text-gray-500 dark:text-gray-400">
              {selectedTickets.length} of {tickets.length} selected
            </span>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-x-auto">
        <table
          className={`min-w-full ${
            darkMode ? "text-gray-200" : "text-gray-800"
          }`}
        >
          <thead className={`bg-gray-50 dark:bg-gray-700`}>
            <tr>
              <th className="px-4 py-3 text-left">
                <input
                  type="checkbox"
                  checked={selectAll}
                  onChange={handleSelectAllChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Ticket ID
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Name
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Email
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Subject
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Date
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Status
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-600">
            {tickets.map((ticket) => (
              <tr
                key={ticket.id}
                className={`${
                  selectedTickets.includes(ticket.id)
                    ? "bg-blue-50 dark:bg-blue-900/20"
                    : ""
                }`}
              >
                <td className="px-4 py-3">
                  <input
                    type="checkbox"
                    checked={selectedTickets.includes(ticket.id)}
                    onChange={() => handleCheckboxChange(ticket.id)}
                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                </td>
                <td
                  className={`px-4 py-3 whitespace-nowrap ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {ticket.id.slice(0, 8)}
                </td>
                <td
                  className={`px-4 py-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {ticket.name}
                </td>
                <td
                  className={`px-4 py-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {ticket.email}
                </td>
                <td
                  className={`px-4 py-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {ticket.subject}
                </td>
                <td
                  className={`px-4 py-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  {ticket.createdAt}
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-1 rounded-full text-xs ${
                      ticket.status === "Open"
                        ? "bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100"
                        : "bg-gray-100 text-gray-800 dark:bg-gray-600 dark:text-gray-200"
                    }`}
                  >
                    {ticket.status}
                  </span>
                </td>
                <td
                  className={`px-4 py-3 ${
                    darkMode ? "text-gray-200" : "text-gray-800"
                  }`}
                >
                  <button
                    className="text-blue-600 hover:text-blue-800 mr-2 dark:text-blue-400 dark:hover:text-blue-500"
                    onClick={() => {
                      // Implement view functionality
                      alert(`Message: ${ticket.message}`);
                    }}
                  >
                    View
                  </button>
                  <button
                    className="text-green-600 hover:text-green-800 mr-2 dark:text-green-400 dark:hover:text-green-500"
                    onClick={() =>
                      handleStatusUpdate(
                        ticket.id,
                        ticket.status === "Open" ? "Closed" : "Open"
                      )
                    }
                  >
                    {ticket.status === "Open" ? "Close" : "Reopen"}
                  </button>
                  <button
                    className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-500"
                    onClick={() => handleTicketDelete(ticket.id)}
                  >
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {tickets.length === 0 && (
          <div className="text-center py-6 text-gray-500">
            No support tickets found
          </div>
        )}
      </div>
    </div>
  );
};

// Original DashboardContent Component (stats and charts)
const DashboardContent = ({ onTabChange }) => {
  const [totalBalance, setTotalBalance] = useState(0);
  const [eventCount] = useState(3);
  const [bookingCount, setBookingCount] = useState(0);
  const [transactionCount, setTransactionCount] = useState(0);
  const [eventIncome, setEventIncome] = useState([]);
  const [monthlyBookings, setMonthlyBookings] = useState([]);

  const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const user = auth.currentUser;
        if (!user) return;

        // Fetch transactions and aggregate by event
        const transactionsQuery = query(collection(db, "orders"));
        const transactionsSnapshot = await getDocs(transactionsQuery);

        const eventIncomeMap = {};
        let totalAmount = 0;

        transactionsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const amount = data.ticketPrice || 0;
          totalAmount += amount;

          if (data.eventTitle) {
            eventIncomeMap[data.eventTitle] =
              (eventIncomeMap[data.eventTitle] || 0) + amount;
          }
        });

        const eventIncomeData = Object.entries(eventIncomeMap).map(
          ([name, value]) => ({
            name,
            value,
          })
        );

        setEventIncome(eventIncomeData);
        setTotalBalance(totalAmount);
        setTransactionCount(transactionsSnapshot.docs.length);

        // Fetch bookings and aggregate by month
        const bookingsQuery = query(collection(db, "verifiedPayments"));
        const bookingsSnapshot = await getDocs(bookingsQuery);

        const monthlyBookingsMap = {};
        bookingsSnapshot.docs.forEach((doc) => {
          const data = doc.data();
          const date = new Date(data.timestamp?.toDate() || new Date());
          const monthYear = date.toLocaleString("default", {
            month: "short",
            year: "numeric",
          });
          monthlyBookingsMap[monthYear] =
            (monthlyBookingsMap[monthYear] || 0) + 1;
        });

        const monthlyBookingsData = Object.entries(monthlyBookingsMap).map(
          ([month, bookings]) => ({
            month,
            bookings,
          })
        );

        setMonthlyBookings(monthlyBookingsData);
        setBookingCount(bookingsSnapshot.docs.length);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div>
      <h2 className="text-2xl mb-4 text-gray-800">Welcome back, organizer!</h2>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">My Balance</h3>
            <p className="text-2xl font-bold text-gray-800">₹{totalBalance}</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => onTabChange("event-management")}
          className="cursor-pointer hover:bg-gray-50 transition"
        >
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">Events</h3>
            <p className="text-2xl font-bold text-gray-800">{eventCount}</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => onTabChange("event-bookings")}
          className="cursor-pointer hover:bg-gray-50 transition"
        >
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Total Event Bookings
            </h3>
            <p className="text-2xl font-bold text-gray-800">{bookingCount}</p>
          </CardContent>
        </Card>
        <Card
          onClick={() => onTabChange("transactions")}
          className="cursor-pointer hover:bg-gray-50 transition"
        >
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-gray-500">
              Total Transaction
            </h3>
            <p className="text-2xl font-bold text-gray-800">
              {transactionCount}
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <Card>
          <CardContent className="p-4">
            <h3 className="text-lg font-medium mb-4">Revenue by Event</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={eventIncome}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, percent }) =>
                      `${name} (₹${(percent * totalBalance).toFixed(0)})`
                    }
                    outerRadius={100}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {eventIncome.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => `₹${value}`} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <h3 className="text-lg font-medium mb-4">Monthly Event Bookings</h3>
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthlyBookings}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="bookings" fill="#8884d8" name="Bookings" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default OrganizerDashboard;
